import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Satellite, UserPlus, CheckCircle } from "lucide-react";
import { api } from "@/lib/api";

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", business_name: "", email: "", mobile: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!/^[6-9]\d{9}$/.test(form.mobile)) {
      toast.error("Please enter a valid 10-digit Indian mobile number");
      return;
    }
    
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/register", form);
      setShowModal(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EEF4FB] via-white to-[#E8F5EF] p-6">
      <div className="w-full max-w-xl bg-white border border-slate-200 rounded-2xl shadow-xl p-10">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-md bg-gradient-to-br from-brand-primary to-brand-accent flex items-center justify-center">
            <Satellite className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading font-bold text-xl text-brand-nav">DishLCO</span>
        </Link>
        <h2 className="font-heading font-bold text-3xl text-brand-nav tracking-tight">Register as an LCO</h2>
        <p className="text-slate-500 mt-2">Your account will go through a quick approval before your white-label site is provisioned.</p>
        <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Business name</label>
            <input required value={form.business_name} onChange={set("business_name")} data-testid="register-business-input"
                   className="w-full border border-slate-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" placeholder="Skylink DTH Services" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Your name</label>
            <input required value={form.name} onChange={set("name")} data-testid="register-name-input"
                   className="w-full border border-slate-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" placeholder="Rahul Kapoor" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Mobile Number *</label>
            <input required value={form.mobile} onChange={set("mobile")} type="tel" maxLength={10} data-testid="register-mobile-input"
                   className="w-full border border-slate-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" placeholder="9812345678" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Email (Optional)</label>
            <input type="email" value={form.email} onChange={set("email")} data-testid="register-email-input"
                   className="w-full border border-slate-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" placeholder="you@operator.com" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
            <input required type="password" minLength={6} value={form.password} onChange={set("password")} data-testid="register-password-input"
                   className="w-full border border-slate-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" placeholder="At least 6 characters" />
          </div>
          <button type="submit" disabled={loading} data-testid="register-submit-button"
                  className="md:col-span-2 bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold py-2.5 rounded-md shadow-sm transition-all flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? "Registering..." : <><UserPlus className="w-4 h-4" /> Register</>}
          </button>
        </form>
        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-slate-500 hover:text-brand-primary font-semibold transition-all">
            ← Back to Homepage
          </Link>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl text-center animate-in zoom-in-95 duration-200">
            <div className="w-20 h-20 bg-green-50 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10" />
            </div>
            <h3 className="font-heading font-bold text-2xl text-brand-nav mb-2">Registration Successful!</h3>
            <p className="text-slate-500 text-[15px] leading-relaxed">
              Your LCO account has been created successfully. It is currently pending review by our administration team. You will be notified once it is approved and your white-label site is provisioned.
            </p>
            <button onClick={() => navigate("/")} className="mt-8 w-full bg-brand-primary hover:bg-brand-primaryHover text-white font-bold py-3 rounded-xl shadow-md transition-all">
              Return to Homepage
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
